<template>
  <!--
    بخش «آمار لیگ‌ها» — پایین صفحهٔ اصلی.
    فقط سه کارت: آقای گل، پاس گل، کلین شیت (بقیهٔ گروه‌های بک‌اند فیلتر می‌شوند).
    لیگ‌ها با select سوییچ می‌شوند. عرض بخش = عرض کامپوننت‌های بالایی.
  -->
  <section class="league-stats-section">

    <div class="section-header">

      <h2 class="section-title">
        <i class="fas fa-chart-bar"></i>
        آمار لیگ‌ها
      </h2>

      <select
          v-model="leagueId"
          class="league-select"
          @change="loadStats(true)"
      >
        <option
            v-for="lg in allLeagues"
            :key="'s-' + lg.id"
            :value="lg.id"
        >
          {{ leagueLabel(lg) }}
        </option>
      </select>

    </div>

    <!-- لودینگ اولیه -->
    <div v-if="loading && !hasGroups" class="stats-loading">
      <div class="spinner"></div>
    </div>

    <!-- کارت‌های آمار -->
    <div
        v-else-if="hasGroups"
        class="stats-grid"
        :class="{ 'is-switching': loading }"
    >

      <!-- کارت‌های بازیکن -->
      <div
          v-for="group in allGroups"
          :key="group.key"
          class="stat-card"
      >

        <div class="stat-card-header">
          <i :class="icon(group.key)"></i>
          <span class="stat-card-title">{{ group.title }}</span>
        </div>

        <div
            v-for="(it, i) in group.items"
            :key="group.key + '-' + (it.id ?? i)"
            class="stat-row"
            :class="{ 'is-team': isTeamGroup(group), linkable: rowLink(group, it) }"
            @click="rowClick(group, it)"
        >

          <span class="stat-rank" :class="{ top: i < 3 }">{{ faNum(i + 1) }}</span>

          <!-- بازیکن: پرتره/آواتار + نام + تیم -->
          <template v-if="!isTeamGroup(group)">
            <span class="stat-avatar">
              <img
                  v-if="it.portrait"
                  :src="it.portrait"
                  :alt="it.name"
                  loading="lazy"
              >
              <span v-else class="avatar-fallback">{{ (it.name || '؟').trim().charAt(0) }}</span>
            </span>

            <span class="stat-main">
              <span class="stat-name">{{ faPlayer(it.name) }}</span>
              <span class="stat-team">
                <img
                    v-if="it.teamLogo"
                    :src="it.teamLogo"
                    :alt="it.teamName || ''"
                    loading="lazy"
                >
                <span>{{ faTeam(it.teamNameFull || it.teamName) || '—' }}</span>
              </span>
            </span>
          </template>

          <!-- تیم: لوگو + نام -->
          <template v-else>
            <span class="stat-avatar team">
              <img
                  v-if="it.logo"
                  :src="it.logo"
                  :alt="it.name"
                  loading="lazy"
              >
              <span v-else class="avatar-fallback">{{ (it.name || '؟').trim().charAt(0) }}</span>
            </span>

            <span class="stat-main">
              <span class="stat-name">{{ faTeam(it.name) }}</span>
            </span>
          </template>

          <span class="stat-value">
            {{ faNum(it.value) }}
          </span>

        </div>

      </div>

    </div>

    <!-- خالی / خطا -->
    <div v-else class="stats-empty">
      <i class="fas fa-chart-bar"></i>
      آماری برای این لیگ ثبت نشده است
    </div>

  </section>
</template>

<script>
import apiService from '@/services/api'
import {
  teamNameFa,
  playerNameFa,
  leagueNames,
  leagueNamesLatin,
} from '@/utils/translations'

// آیکن هر گروه آماری (کلیدهای بک‌اند) — بدون رنگ؛ همهٔ کارت‌ها یکدست‌اند
const GROUP_META = {
  goals:            { icon: 'fas fa-futbol' },
  assists:          { icon: 'fas fa-hands' },
  cleanSheets:      { icon: 'fas fa-mitten' },
  goalsPlusAssists: { icon: 'fas fa-star' },
  penalties:        { icon: 'fas fa-crosshairs' },
  mostGoals:        { icon: 'fas fa-fire' },
  bestDefense:      { icon: 'fas fa-shield-alt' },
  mostWins:         { icon: 'fas fa-trophy' },
  teamCleanSheets:  { icon: 'fas fa-mitten' },
}

const FALLBACK_META = { icon: 'fas fa-chart-bar' }

// گروه‌های آمار تیمی (برای نحوهٔ نمایش ردیف)
const TEAM_KEYS = new Set(['mostGoals', 'bestDefense', 'mostWins', 'teamCleanSheets'])

// فقط این سه کارت نمایش داده می‌شوند — بقیهٔ گروه‌های سرور حذف (درخواست کاربر)
const VISIBLE_GROUP_KEYS = new Set(['goals', 'assists', 'cleanSheets'])

export default {

  name: 'LeagueStats',

  data() {
    return {
      fallbackLeagues: [],
      leagueId: null,
      stats: null,        // payload سرور برای لیگ انتخابی
      loading: false,
      cache: {},          // کلید: leagueId → payload
    }
  },

  computed: {
    leagues() {
      return this.$store.getters.competitions || []
    },

    allLeagues() {
      return this.leagues.length ? this.leagues : this.fallbackLeagues
    },

    hasGroups() {
      return this.allGroups.length > 0
    },

    // ترتیب نمایش: فقط آقای گل، پاس گل، کلین شیت
    allGroups() {
      if (!this.stats) return []
      return [...(this.stats.playerStats || []), ...(this.stats.teamStats || [])]
          .filter(g => VISIBLE_GROUP_KEYS.has(g.key))
    },
  },

  watch: {
    // وقتی لیست لیگ‌ها از استور رسید و هنوز چیزی انتخاب نشده، پیش‌فرض را بگذار
    allLeagues(list) {
      if (list.length && !this.leagueId) {
        this.initDefaults()
      }
    },
  },

  async mounted() {
    // اگر استور خالی بود (تأخیر یا خطا)، مستقیم از سرور
    if (!this.leagues.length) {
      try {
        const res = await apiService.getTopLeagues()
        this.fallbackLeagues = Array.isArray(res) ? res : []
      } catch (e) {
        console.error('LeagueStats: leagues fetch failed', e)
      }
    }
    if (this.allLeagues.length) {
      this.initDefaults()
    }
  },

  methods: {
    initDefaults() {
      if (!this.allLeagues.length || this.leagueId) return
      // لیگ برتر ایران اول لیست است (اولویت بک‌اند) → پیش‌فرض همان
      this.leagueId = this.allLeagues[0].id
      this.loadStats(false)
    },

    leagueLabel(lg) {
      return leagueNames[lg.shortName] || leagueNamesLatin[lg.name] || lg.name
    },

    faTeam(name) {
      return teamNameFa(name)
    },

    faPlayer(name) {
      return playerNameFa(name)
    },

    // اعداد فارسی — هم عدد، هم رشته‌هایی مثل «2 / 2»
    faNum(v) {
      return String(v ?? '').replace(/[0-9]/g, d => '۰۱۲۳۴۵۶۷۸۹'[Number(d)])
    },

    isTeamGroup(group) {
      return TEAM_KEYS.has(group.key)
    },

    icon(key) {
      return (GROUP_META[key] || FALLBACK_META).icon
    },

    // لینک ردیف — تیم یا بازیکن (null = غیرقابل کلیک)
    // بازیکن: اگر اسلاگ دیتابیس دارد (بک‌اند الحاق می‌کند) → /player/{slug}
    // هماهنگ با لینک‌های صفحهٔ تیم/بازی/خبر و canonical؛
    // وگرنه فچ همان‌لحظه‌ای با پیشوند v3- (ورزش۳) یا fd- (فوتبال‌دیتا)
    rowLink(group, it) {
      if (this.isTeamGroup(group)) {
        return (group.linkable && it.teamId) ? `/team/${it.teamId}` : null
      }
      if (it.id == null) return null
      const src = this.stats?.source
      if (src === 'varzesh3') return it.slug ? `/player/${it.slug}` : `/player/v3-${it.id}`
      if (src === 'football-data') return it.slug ? `/player/${it.slug}` : `/player/fd-${it.id}`
      return null
    },

    rowClick(group, it) {
      const link = this.rowLink(group, it)
      if (link) this.$router.push(link)
    },

    async loadStats(fromUser) {
      const id = this.leagueId
      if (!id) return

      // کش محلی کامپوننت — سوییچ سریع بین لیگ‌ها
      if (this.cache[id]) {
        this.stats = this.cache[id]
        return
      }

      this.loading = true
      if (fromUser) this.stats = null
      try {
        const res = await apiService.request(`/competition/${id}/stats`)
        // پاسخ ممکن است مربوط به لیگ دیگری رسیده باشد (سوییچ سریع)
        if (this.leagueId !== id) return
        this.cache[id] = res || { playerStats: [], teamStats: [] }
        this.stats = this.cache[id]
      } catch (e) {
        console.error('LeagueStats: stats fetch failed', e)
        if (this.leagueId === id) this.stats = { playerStats: [], teamStats: [] }
      } finally {
        if (this.leagueId === id) this.loading = false
      }
    },
  },

}
</script>

<style scoped lang="scss">
@import '@/assets/sass/variables';
@import '@/assets/sass/mixin';

.league-stats-section {
  margin-block: 42px;
  padding: 10px 10px 30px;
  background-color: #f3f3f3;
  border-radius: 8px;
}

/* -------------------------
   HEADER
------------------------- */
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 18px;
}

.section-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  gap: 10px;

  i {
    color: $primary-blue;
  }
}

/* سلکت لیگ — هم‌خانوادهٔ سلکت سایدبار */
.league-select {
  min-width: 200px;
  max-width: 260px;
  padding: 9px 12px;
  border: 1px solid $gray-200;
  border-radius: $border-radius-sm;
  background: #fff;
  color: $gray-700;
  font-family: inherit;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  outline: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    border-color: $secondary-blue;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.08);
  }
}

/* -------------------------
   LOADING / EMPTY
------------------------- */
.stats-loading {
  padding: 46px 0;
  text-align: center;
}

.spinner {
  width: 30px;
  height: 30px;
  border: 3px solid $gray-200;
  border-top-color: $primary-blue;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.stats-empty {
  text-align: center;
  padding: 40px 10px;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.06);
  color: $gray-400;
  font-size: 0.85rem;

  i {
    display: block;
    font-size: 1.7rem;
    margin-bottom: 10px;
  }
}

/* -------------------------
   GRID
------------------------- */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  gap: 14px;
  align-items: start;
  transition: opacity 0.2s;

  &.is-switching {
    opacity: 0.45;
    pointer-events: none;
  }
}

/* -------------------------
   STAT CARD
------------------------- */
.stat-card {
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.07);
  overflow: hidden;
}

/* سربرگ یکدست سرمه‌ای — هم‌خانوادهٔ هدر جدول رده‌بندی */
.stat-card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: #0f172a;
  color: #fff;
  font-size: 0.85rem;
  font-weight: 800;

  i {
    font-size: 0.9rem;
    opacity: 0.85;
  }
}

/* -------------------------
   ROWS
------------------------- */
.stat-row {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 9px 12px;
  border-bottom: 1px solid $gray-100;

  &:last-child {
    border-bottom: none;
  }

  &.linkable {
    cursor: pointer;
    transition: background 0.2s;

    &:hover {
      background: $gray-50;

      .stat-name {
        color: $primary-blue;
      }
    }
  }
}

.stat-rank {
  width: 16px;
  text-align: center;
  font-size: 0.75rem;
  font-weight: 800;
  color: $gray-400;
  flex-shrink: 0;

  &.top {
    color: $primary-blue;
  }
}

.stat-avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background: $gray-100;
  @include flex-center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .avatar-fallback {
    font-size: 0.8rem;
    font-weight: 800;
    color: $gray-500;
  }

  &.team {
    border-radius: 50%;
    background: #fff;
    border: 1px solid $gray-100;

    img {
      object-fit: contain;
    }
  }
}

.stat-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.stat-name {
  font-size: 0.8rem;
  font-weight: 700;
  color: $gray-800;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: color 0.2s;
}

.stat-team {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.7rem;
  color: $gray-500;

  img {
    width: 13px;
    height: 13px;
    object-fit: contain;
    flex-shrink: 0;
  }

  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

/* بج مقدار — آبی ملایم هم‌خانوادهٔ رنگ اصلی سایت */
.stat-value {
  min-width: 30px;
  height: 26px;
  padding: 0 8px;
  border-radius: 13px;
  @include flex-center;
  background: rgba(37, 99, 235, 0.08);
  color: $secondary-blue;
  font-size: 0.78rem;
  font-weight: 800;
  flex-shrink: 0;
}

/* -------------------------
   MOBILE
------------------------- */
@media (max-width: 768px) {
  .league-stats-section {
    margin-block: 30px;
  }

  .section-header {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
    margin-bottom: 14px;
  }

  .section-title {
    font-size: 1.05rem;
    justify-content: center;
  }

  .league-select {
    min-width: 0;
    max-width: none;
    width: 100%;
  }

  .stats-grid {
    grid-template-columns: 1fr;
    gap: 10px;
  }
}
</style>
